import { type FC, type KeyboardEvent, useState } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { type VocabularyTerm } from './vocabularyTypes'
import styles from './VocabularyTermRow.module.scss'

const EDIT_LABEL = 'Edit term'
const REMOVE_LABEL = 'Remove term'

interface VocabularyTermRowProps {
  term: VocabularyTerm
  isLast: boolean
  isEditing: boolean
  isSaving: boolean
  error: string | null
  readOnly?: boolean
  busy?: boolean
  justAdded?: boolean
  onEdit: (term: VocabularyTerm) => void
  onCancelEdit: () => void
  onRename: (term: VocabularyTerm, newTerm: string) => void
  onRemove: (term: VocabularyTerm) => void
}

export const VocabularyTermRow: FC<VocabularyTermRowProps> = ({
  term,
  isLast,
  isEditing,
  isSaving,
  error,
  readOnly = false,
  busy = false,
  justAdded = false,
  onEdit,
  onCancelEdit,
  onRename,
  onRemove,
}) => {
  const [draft, setDraft] = useState(term.term)
  const [submittedDraft, setSubmittedDraft] = useState<string | null>(null)
  const visibleError = error && draft === submittedDraft ? error : null

  const startEditing = () => {
    setDraft(term.term)
    setSubmittedDraft(null)
    onEdit(term)
  }

  const save = () => {
    if (draft.trim() === term.term) {
      onCancelEdit()
      return
    }
    setSubmittedDraft(draft)
    onRename(term, draft)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      save()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancelEdit()
    }
  }

  return (
    <li
      data-just-added={justAdded ? 'true' : undefined}
      className={classNames('d-flex flex-column zocks-px-0.75 py-3', { 'border-bottom zocks-border-color-gray-400': !isLast, [styles.justAdded]: justAdded })}
    >
      <div className='d-flex align-items-center justify-content-between gap-3'>
        {isEditing && !readOnly ? (
          <>
            <Form.Control
              type='text'
              autoFocus
              value={draft}
              isInvalid={!!visibleError}
              disabled={isSaving}
              aria-label={EDIT_LABEL}
              className='paragraph-medium'
              onChange={(event) => setDraft(event.currentTarget.value)}
              onKeyDown={handleKeyDown}
            />
            <div className='d-flex align-items-center gap-2 flex-shrink-0'>
              <Button type='button' variant='primary' loading={isSaving} onClick={save}>
                Save
              </Button>
              <Button type='button' variant='secondary' disabled={isSaving} onClick={onCancelEdit}>
                Discard
              </Button>
            </div>
          </>
        ) : (
          <>
            <span className='paragraph-medium zocks-text-gray-900 text-break'>{term.term}</span>
            {readOnly ? null : (
              <div className='d-flex align-items-center gap-3 flex-shrink-0'>
                <Tooltip title={EDIT_LABEL} placement='top'>
                  <button type='button' className='btn p-0 border-0 d-flex zocks-text-gray-900' aria-label={EDIT_LABEL} disabled={busy} onClick={startEditing}>
                    <i className='bi bi-pencil' aria-hidden='true' />
                  </button>
                </Tooltip>
                <Tooltip title={REMOVE_LABEL} placement='top'>
                  <button
                    type='button'
                    className='btn p-0 border-0 d-flex zocks-text-gray-900'
                    aria-label={REMOVE_LABEL}
                    disabled={busy}
                    onClick={() => onRemove(term)}
                  >
                    <i className='bi bi-trash' aria-hidden='true' />
                  </button>
                </Tooltip>
              </div>
            )}
          </>
        )}
      </div>
      {visibleError ? <FormFeedback className='paragraph-small mt-1'>{visibleError}</FormFeedback> : null}
    </li>
  )
}
